import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from '@fluentui/react-hooks';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Input, MessageBarActions, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { Add20Regular, LockClosed16Regular, LockOpen16Regular, Search20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { TenantPriceList } from '../../../../models/Entities/TenantPriceLists/TenantPriceList';
import { onFormatDate, parseUTCDateAsLocal } from '../../../../commons/functions/dateUtils';
import TenantPriceListFormComponent from '../TenantPriceListForm/TenantPriceListFormComponent';

// guía
import { GuideTable, GuideTh, GuideRowActions, guideCellClassName as cell, guideRowClassName, useGuideSort } from '../../../../theme/components/GuideTable';
import { GuidePagination, useGuidePagination } from '../../../../theme/components/GuidePagination';
import { GuideEmptyState } from '../../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../../theme/components/GuideSkeleton';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { GuideBannerAction, GuideLinkButton } from '../../../../theme/components/GuideButtons';
import { GuideCountChip } from '../../../../theme/components/GuideKpiCard';
import { GuideTooltip } from '../../../../theme/components/GuideTooltip';
import { StatusPill } from '../../../../theme/components/StatusPill';

// lógica
import { priceListStatus } from '../../PriceList.Logic';

// Functions
import * as ComponentFunctions from './TenantPriceListFunctions';

// Styles
import './TenantPriceList.css';

const PAGE_SIZES = [10, 25, 50];

const fold = (text: string | null | undefined): string => (text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const formatDate = (value: Date | string | undefined | null): string | undefined => {
  if (!value) return undefined;
  const parsed = parseUTCDateAsLocal(value as Date);
  return parsed ? onFormatDate(parsed) : undefined;
};

/**
 * Listas de precios del tenant (guía): barra con búsqueda, contador y "Agregar"; grilla con
 * nombre y tipo, estado de vigencia, comentario y fechas, acciones en ⋯ (Ver detalle, Editar,
 * Eliminar con confirmación) y paginación. Click en la fila abre el detalle.
 */
export const TenantPriceListComponent = (props: ComponentFunctions.Props) => {
  const navigate = useNavigate();
  const [t] = useTranslation();

  const [selectedTenantPriceList, setSelectedTenantPriceList] = useState<TenantPriceList | undefined>(undefined);
  const [filter, setFilter] = useState('');
  const [deleting, setDeleting] = useState<TenantPriceList | undefined>(undefined);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);

  const load = () => { props.GetAllTenantPriceList(); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // El diálogo de borrado se cierra solo cuando la operación terminó bien.
  useEffect(() => {
    if (deleteRequested && !props.isDeleting && !props.failOnDeleting) {
      setDeleting(undefined);
      setDeleteRequested(false);
    }
  }, [deleteRequested, props.isDeleting, props.failOnDeleting]);

  const allPriceLists = useMemo(() => props.TenantPriceLists || [], [props.TenantPriceLists]);

  const visiblePriceLists = useMemo(() => {
    const term = fold(filter).trim();
    if (!term) return allPriceLists;
    return allPriceLists.filter((item) => fold(item.priceList?.listName).includes(term) || fold(item.priceList?.comments).includes(term));
  }, [allPriceLists, filter]);

  const { rows, sortProps } = useGuideSort<TenantPriceList>(visiblePriceLists, {
    name: (item) => item.priceList?.listName,
    status: (item) => priceListStatus(item).key,
    validSince: (item) => (item.effectiveDateStart ? new Date(item.effectiveDateStart).getTime() : undefined),
    validUntil: (item) => (item.effectiveDateEnd ? new Date(item.effectiveDateEnd).getTime() : undefined)
  }, { key: 'name', direction: 'asc' });
  const { pageRows, paginationProps } = useGuidePagination(rows, 25);

  // events
  const onView = (list: TenantPriceList) => navigate('/tenantpricelist/' + list.id);
  const onAdd = () => { setSelectedTenantPriceList(undefined); openPanel(); };
  const onEdit = (list: TenantPriceList) => { setSelectedTenantPriceList(list); openPanel(); };
  const onSearch = (value: string) => { setFilter(value); paginationProps.onPageChange(1); };

  const isSearching = filter.trim().length > 0;
  const showSkeleton = !!props.isGettingAll && allPriceLists.length === 0;

  const renderRow = (item: TenantPriceList) => {
    const status = priceListStatus(item);
    const since = formatDate(item.effectiveDateStart);
    const until = formatDate(item.effectiveDateEnd);
    return (
      <tr
        key={item.id}
        className={guideRowClassName({ inactive: status.key === 'expired' }, 'tpl-row')}
        tabIndex={0}
        onClick={() => onView(item)}
        onKeyDown={(event) => { if (event.key === 'Enter') onView(item); }}
        data-testid={`tpl-row-${item.id}`}
      >
        <td>
          <div className="tpl-name">
            <GuideTooltip content={item.isBlocked ? t('tenantPriceList.blocked.yes') : t('tenantPriceList.blocked.no')} relationship="description">
              <span className={`tpl-lock${item.isBlocked ? ' tpl-lock--closed' : ''}`} aria-label={item.isBlocked ? t('tenantPriceList.blocked.yes') : t('tenantPriceList.blocked.no')}>
                {item.isBlocked ? <LockClosed16Regular /> : <LockOpen16Regular />}
              </span>
            </GuideTooltip>
            <div className="tpl-name__text">
              <span className="tpl-name__title" title={item.priceList?.listName}>{item.priceList?.listName}</span>
              <span className="tpl-name__type">{t(ComponentFunctions.getTenantPriceListTypeKey(item.priceListType))}</span>
            </div>
          </div>
        </td>
        <td><StatusPill tone={status.tone} symbol={status.symbol} size="small">{t(status.i18nKey)}</StatusPill></td>
        <td>
          {item.priceList?.comments
            ? <span className="tpl-comment" title={item.priceList.comments}>{item.priceList.comments}</span>
            : <span className="tpl-muted">—</span>}
        </td>
        <td className={cell('num')}>{since ?? <span className="tpl-muted">{t('tenantPriceList.notDefined')}</span>}</td>
        <td className={cell('num')}>{until ?? <span className="tpl-muted">{t('tenantPriceList.notDefined')}</span>}</td>
        <td className={cell('actions')} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          <GuideRowActions
            label={`${t('tenantPriceList.options')} ${item.priceList?.listName ?? ''}`}
            actions={[
              { key: 'view', label: t('tenantPriceList.buttons.detailButton'), onClick: () => onView(item) },
              { key: 'edit', label: t('tenantPriceList.buttons.editButton'), onClick: () => onEdit(item) },
              { key: 'delete', label: t('tenantPriceList.buttons.deleteButton'), danger: true, onClick: () => { setDeleteRequested(false); setDeleting(item); } }
            ]}
          />
        </td>
      </tr>
    );
  };

  const renderBody = () => {
    if (showSkeleton) return <GuideSkeleton rows={4} label={t('tenantPriceList.loading.priceList')} data-testid="tpl-loading" />;
    if (props.failOnGettingAll && allPriceLists.length === 0) return null;
    if (allPriceLists.length === 0) {
      return (
        <GuideEmptyState
          title={t('tenantPriceList.state.emptyTitle', { defaultValue: 'Todavía no hay listas de precios' })}
          text={t('tenantPriceList.state.empty')}
          action={<Button appearance="primary" icon={<Add20Regular />} onClick={onAdd}>{t('tenantPriceList.buttons.addPriceList')}</Button>}
          data-testid="tpl-empty"
        />
      );
    }
    if (visiblePriceLists.length === 0) {
      return (
        <GuideEmptyState
          title={t('tenantPriceList.state.noMatches', { term: filter.trim() })}
          action={<GuideLinkButton onClick={() => onSearch('')}>{t('tenantPriceList.state.clearSearch', { defaultValue: 'Limpiar búsqueda' })}</GuideLinkButton>}
          data-testid="tpl-no-match"
        />
      );
    }
    return (
      <GuideTable
        ariaLabel={t('tenantPriceList.title')}
        data-testid="tpl-table"
        footer={<GuidePagination {...paginationProps} pageSizeOptions={PAGE_SIZES} />}
      >
        <thead>
          <tr>
            <GuideTh sort={sortProps('name')}>{t('tenantPriceList.name')}</GuideTh>
            <GuideTh sort={sortProps('status')}>{t('tenantPriceList.status.label')}</GuideTh>
            <GuideTh>{t('tenantPriceList.comment')}</GuideTh>
            <GuideTh sort={sortProps('validSince')}>{t('tenantPriceList.validSince')}</GuideTh>
            <GuideTh sort={sortProps('validUntil')}>{t('tenantPriceList.validityUntil')}</GuideTh>
            <GuideTh className={cell('actions')}><span className="tpl-sr-only">{t('tenantPriceList.options')}</span></GuideTh>
          </tr>
        </thead>
        <tbody>{pageRows.map(renderRow)}</tbody>
      </GuideTable>
    );
  };

  return (
    <section className="tpl" data-testid="tenant-price-lists">
      <div className="tpl-toolbar">
        <Input
          type="search"
          className="tpl-search"
          contentBefore={<Search20Regular />}
          placeholder={t('tenantPriceList.state.search')}
          aria-label={t('tenantPriceList.state.search')}
          value={filter}
          maxLength={200}
          onChange={(_event, data) => onSearch(data.value)}
          data-testid="tpl-search"
        />
        <div className="tpl-toolbar__counters">
          <GuideCountChip label={t('tenantPriceList.title')} count={allPriceLists.length} />
          {isSearching && <GuideCountChip label={t('tenantPriceList.state.matching', { defaultValue: 'Coinciden' })} count={visiblePriceLists.length} className="tpl-chip--match" />}
          {props.isGettingAll && allPriceLists.length > 0 && <Spinner size="extra-tiny" aria-label={t('tenantPriceList.loading.priceList')} />}
        </div>
        <div className="tpl-toolbar__actions">
          <Button appearance="primary" icon={<Add20Regular />} onClick={onAdd} data-testid="tpl-add">
            {t('tenantPriceList.buttons.addPriceList')}
          </Button>
        </div>
      </div>

      {props.failOnGettingAll && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="tpl-load-error">
          <MessageBarBody>
            <MessageBarTitle>{t('tenantPriceList.state.loadFailed')}</MessageBarTitle>
            {props.error?.ErrorMessage}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={load}>{t('tenantPriceList.state.retry', { defaultValue: 'Reintentar' })}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      {renderBody()}

      <TenantPriceListFormComponent
        isOpen={isOpen}
        openPanel={() => openPanel()}
        dismissPanel={() => { setSelectedTenantPriceList(undefined); dismissPanel(); }}
        TenantPriceList={selectedTenantPriceList}
      />

      <Dialog open={!!deleting} modalType="alert" onOpenChange={(_event, data) => { if (!data.open && !props.isDeleting) { setDeleting(undefined); setDeleteRequested(false); } }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('tenantPriceList.buttons.title')}</DialogTitle>
            <DialogContent>
              <p className="tpl-dialog__text">{t('tenantPriceList.buttons.description')}</p>
              <p className="tpl-dialog__name">
                <span className="tpl-dialog__label">{t('tenantPriceList.buttons.priceList')}</span> {deleting?.priceList?.listName}
              </p>
              {deleteRequested && props.failOnDeleting && (
                <GuideMessageBar intent="error" layout="multiline" data-testid="tpl-delete-error">
                  <MessageBarBody>
                    <MessageBarTitle>{t('tenantPriceList.props.errorMessage')}</MessageBarTitle>
                    {props.error?.ErrorMessage}
                  </MessageBarBody>
                </GuideMessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => { setDeleting(undefined); setDeleteRequested(false); }} disabled={!!props.isDeleting}>
                {t('tenantPriceList.buttons.cancelButton')}
              </Button>
              <Button
                appearance="primary"
                className="tpl-danger"
                disabled={!!props.isDeleting}
                icon={props.isDeleting ? <Spinner size="extra-tiny" /> : undefined}
                onClick={() => { if (deleting) { setDeleteRequested(true); props.DeleteTenantPriceList(deleting.id); } }}
                data-testid="tpl-delete-confirm"
              >
                {t('tenantPriceList.buttons.deleteButton')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(TenantPriceListComponent as any);
